import { useMedia } from 'react-use';

import { CHAIN_IDS } from '@/constants/chain';

import { breakpoints } from '@/styles/util';

import { LiquidityProviderAvatar } from '@/ui/Avatars/LiquidityProviderAvatar';

// utils
import { LiquiditySource, LiquiditySourceMultiHop } from '../../utils/liquidity-sources';
import { RouteDetailsTooltip } from '../TransactionModule/MarketMode/SwapDetailsAccordion/RouteDetailsTooltip';

type Sources = LiquiditySource[] | LiquiditySourceMultiHop[];
interface Props {
  sources: Sources;
  isLoading: boolean;
  availableSourceCount: number;
  chainId: number;
  isWrap: boolean;
}

export const LiquiditySources = ({
  sources,
  isLoading,
  availableSourceCount,
  chainId,
  isWrap,
}: Props) => {
  const isMobile = useMedia(breakpoints.mobile, false);

  if (sources.length === 0) return null;

  // Sort and slice the sources to display only the top 3
  const sortedSources = [...sources].sort(
    (a, b) => Number(b.proportion) - Number(a.proportion),
  ) as Sources;
  const sortedSourcesPreview = sortedSources.slice(0, 3);

  const liquidityGroupTitle = sortedSources
    ? sortedSources?.length > 1
      ? `${sortedSources.length} sources`
      : // '0x' means RFQ on EVM; on Solana it is the 0x race leg's fallback source label
        sortedSources[0].name === '0x' && chainId !== CHAIN_IDS.SOLANA
        ? '0x RFQ'
        : sortedSources[0].name.replace(/_/g, ' ') // ex: Uniswap_V3 --> Uniswap V3
    : '';
  // Derive source icon. Special logic for multiplex and multihop
  let sourceIcons;
  if (sortedSources.length > 1) {
    const sourceCount = sortedSourcesPreview.length;
    // Multiplex (more than 1 liquidity sources): stack source icon
    sourceIcons = (
      <div className="flex items-center justify-center relative ml-20">
        {sortedSourcesPreview.map((source: LiquiditySource, index: number) => {
          return (
            <div
              key={`${source}-${index}`}
              style={{ zIndex: sourceCount - index }}
              className={
                index === sourceCount - 1
                  ? 'flex items-center justify-center relative w-5 h-5 -ml-5'
                  : 'flex items-center justify-center relative w-5 h-5 mr-3.5 -ml-5'
              }
            >
              <LiquidityProviderAvatar name={source.name} size={20} />
            </div>
          );
        })}
      </div>
    );
  } else if (sortedSources[0].name === 'MultiHop' && 'hops' in sortedSources[0]) {
    const hopsCount = sortedSources[0].hops.length;
    // Multihop: stack hop icon
    sourceIcons = (
      <div className="flex items-center justify-center relative ml-20">
        {sortedSources[0].hops.map((hop: string, index: number) => (
          <div
            key={`${hop}-${index}`}
            style={{ zIndex: hopsCount - index }}
            className={
              index === hopsCount - 1
                ? 'flex items-center justify-center relative w-5 h-5 -ml-5'
                : 'flex items-center justify-center relative w-5 h-5 mr-12 -ml-5'
            }
          >
            <LiquidityProviderAvatar name={hop} size={20} />
          </div>
        ))}
      </div>
    );
  } else {
    sourceIcons = (
      <div className="flex items-center justify-center relative w-5 h-5">
        <LiquidityProviderAvatar name={sources[0].name} size={20} />
      </div>
    );
  }

  const button = (
    <div className="flex items-center justify-center relative">
      {sourceIcons}
      <div className="ml-1.5 text-sm font-medium">{liquidityGroupTitle}</div>
    </div>
  );

  if (isMobile) {
    return button;
  }

  return (
    <RouteDetailsTooltip
      availableSourceCount={availableSourceCount}
      tooltipTrigger={button}
      liquiditySources={sources}
      isLoading={isLoading}
      isWrap={isWrap}
      chainId={chainId}
    />
  );
};
